import { type ReactNode, useCallback, useEffect, useState } from "react";
import { useModuleBEM } from "@b2bportal/core-themes";
import type { AutocompleteState } from "@b2bportal/core-ui";
import { useI18nContext } from "@hopper-b2b/i18n";
import type { IApiConfig } from "@hopper-b2b/types";
import { apiConfig, useDebounce } from "@hopper-b2b/utilities";
import { InputAdornment, Popper, type PopperProps } from "@material-ui/core";
import {
  type AutocompleteRenderInputParams,
  Autocomplete as MuiAutocomplete,
} from "@material-ui/lab";
import clsx from "clsx";
import defaultStyles from "./Autocomplete.module.scss";
import { TextInputField } from "./TextInputField";

export type AutocompleteProps<T> = {
  // Input props
  id: string;
  className?: string;
  popperClassName?: string;
  icon?: React.ReactElement;
  defaultValue?: T;
  label: string;
  disabled?: boolean;
  placeholder?: string;
  required?: boolean;
  helperText?: ReactNode;
  // Options props
  staticOptions?: T[];
  sortOptions?: (options?: T[]) => T[];
  filterStaticOptions?: (staticOptions: T[], search: string) => T[];
  getOptionLabel: (option: T) => string;
  groupBy?: (option: T) => string;
  // Search options
  onChange: (value?: T, autocompleteState?: AutocompleteState<T>) => void;
  fetch?: (
    apiConfig: IApiConfig | undefined,
    search: string,
    onSuccess: (newOptions: T[]) => void
  ) => Promise<void>;
  shouldFetch?: (search: string) => boolean;
  getOptionComponent: (option: T) => ReactNode;
};

export const Autocomplete = <T,>(props: AutocompleteProps<T>) => {
  const {
    // Input props
    id,
    defaultValue,
    label,
    disabled,
    placeholder,
    icon,
    className,
    popperClassName,
    // helperText, // TODO: Add helper text support
    // Option props
    staticOptions,
    sortOptions,
    filterStaticOptions,
    getOptionLabel,
    groupBy,
    // Search props
    fetch,
    shouldFetch,
    onChange,
    getOptionComponent,
    // required, // TODO: Add required field validation
  } = props;
  const COMPONENT_KEY = "Autocomplete";
  const [block, cn] = useModuleBEM(defaultStyles, COMPONENT_KEY);

  const { t } = useI18nContext();

  const [open, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [value, setValue] = useState<T>(defaultValue ?? null);
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState<T[]>([]);

  const debouncedSearch = useDebounce(search, 300);

  const canFetch = !shouldFetch || shouldFetch(debouncedSearch);

  useEffect(() => {
    if (!open) {
      setOptions([]);
      return;
    }

    const filteredStaticOptions = filterStaticOptions
      ? filterStaticOptions(staticOptions, debouncedSearch)
      : (staticOptions ?? []);

    if (fetch && canFetch) {
      setLoading(true);
      fetch(apiConfig, debouncedSearch, (newOptions: T[]) => {
        setOptions(
          sortOptions
            ? sortOptions([...filteredStaticOptions, ...newOptions])
            : [...filteredStaticOptions, ...newOptions]
        );
        setLoading(false);
      });
    } else {
      setOptions(
        sortOptions ? sortOptions(filteredStaticOptions) : filteredStaticOptions
      );
    }
  }, [
    fetch,
    canFetch,
    open,
    staticOptions,
    filterStaticOptions,
    debouncedSearch,
    sortOptions,
  ]);

  const handleOnInputChange = useCallback((_: never, newInputValue: string) => {
    setSearch(newInputValue);
  }, []);

  const handleOnValueSelect = useCallback(
    (_: never, newValue: T) => {
      setValue(newValue);
      onChange(newValue, { search, options });
    },
    [onChange, search, options, getOptionLabel]
  );

  const handleOnOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const onFocus = useCallback(() => {
    setSearch("");
  }, []);

  const handleOnClose = useCallback(() => {
    setOpen(false);
  }, []);

  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <TextInputField
        {...params}
        label={label}
        aria-label={label}
        placeholder={placeholder}
        variant="filled"
        InputLabelProps={{
          className: cn("label"),
        }}
        InputProps={{
          ...params.InputProps,
          className: cn("input"),
          disableUnderline: true,
          startAdornment: (
            <>
              {icon && (
                <InputAdornment
                  position="start"
                  className={cn("start-adornment")}
                >
                  {icon}
                </InputAdornment>
              )}
              {params.InputProps.startAdornment}
            </>
          ),
        }}
      />
    ),
    [label, placeholder, cn, icon]
  );

  const AutocompleteCustomPopper = (props: PopperProps) => (
    <Popper
      {...props}
      className={clsx(cn("popper"), popperClassName)}
      placement="bottom-start"
    />
  );

  return (
    <MuiAutocomplete
      id={id}
      open={open && (canFetch || (staticOptions?.length ?? 0) > 0)}
      disabled={disabled}
      onOpen={handleOnOpen}
      onClose={handleOnClose}
      onFocus={onFocus}
      value={value}
      inputValue={search}
      defaultValue={defaultValue}
      options={options}
      loading={loading}
      loadingText={t?.("loading")}
      noOptionsText={t?.("noOptions")}
      classes={{
        paper: clsx(cn("paper")),
      }}
      onChange={handleOnValueSelect}
      onInputChange={handleOnInputChange}
      groupBy={groupBy}
      getOptionLabel={getOptionLabel}
      disableClearable={loading}
      renderInput={renderInput}
      renderOption={getOptionComponent}
      className={clsx(block, className)}
      // Disable UI filtering
      filterOptions={(options: T[]) => options}
      PopperComponent={AutocompleteCustomPopper}
    />
  );
};
